import React, { useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Divider,
  Empty,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
  BackTop,
  message,
  Modal,
  Image,
  Input,
} from "antd";
import {
  FolderOutlined,
  FolderAddOutlined,
  FileOutlined,
  FileImageOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FileZipOutlined,
  CodeOutlined,
  VideoCameraOutlined,
  AudioOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  HomeOutlined,
  ReloadOutlined,
  CopyOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useAuth0 } from "../../react-auth0-spa";
import config from "../../api_config.json";
import { useAppContext } from "../../context";
import CdnUploader from "./CdnUploader";

const { Title, Text } = Typography;

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
};

// Get icon based on file type
const getFileIcon = (type, size = 24) => {
  const iconProps = { style: { fontSize: size } };
  
  switch (type) {
    case 'folder':
      return <FolderOutlined {...iconProps} style={{ ...iconProps.style, color: '#1890ff' }} />;
    case 'image':
      return <FileImageOutlined {...iconProps} style={{ ...iconProps.style, color: '#52c41a' }} />;
    case 'video':
      return <VideoCameraOutlined {...iconProps} style={{ ...iconProps.style, color: '#722ed1' }} />;
    case 'pdf':
      return <FilePdfOutlined {...iconProps} style={{ ...iconProps.style, color: '#f5222d' }} />;
    case 'document':
      return <FileTextOutlined {...iconProps} style={{ ...iconProps.style, color: '#fa8c16' }} />;
    case 'code':
      return <CodeOutlined {...iconProps} style={{ ...iconProps.style, color: '#13c2c2' }} />;
    case 'archive':
      return <FileZipOutlined {...iconProps} style={{ ...iconProps.style, color: '#faad14' }} />;
    case 'audio':
      return <AudioOutlined {...iconProps} style={{ ...iconProps.style, color: '#eb2f96' }} />;
    default:
      return <FileOutlined {...iconProps} />;
  }
};

const CDNBrowser = () => {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [continuationToken, setContinuationToken] = useState(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [showUploader, setShowUploader] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const { isProd } = useAppContext();
  const { getTokenSilently } = useAuth0();

  // Parse the current path from URL
  const getCurrentPath = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('path') || '';
  }, [location.search]);

  // Fetch items from the API
  const fetchItems = useCallback(async (path, token = null) => {
    if (!token) {
      setLoading(true);
      setItems([]);
    } else {
      setLoadingMore(true);
    }

    try {
      const accessToken = await getTokenSilently();
      const params = new URLSearchParams({
        prefix: path,
      });
      
      if (token) {
        params.append('continuationToken', token);
      }

      const response = await fetch(
        `${config.baseUrl[isProd ? "prod" : "dev"]}/cdn-files?${params}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const newItems = data.data.items || [];
        
        if (!token) {
          setItems(newItems);
        } else {
          setItems(prev => [...prev, ...newItems]);
        }
        
        setBreadcrumbs(data.data.breadcrumbs || []);
        setIsTruncated(data.data.isTruncated || false);
        setContinuationToken(data.data.nextContinuationToken || null);
      } else {
        const errorText = await response.text();
        console.error("Failed to fetch CDN files:", response.status, errorText);
      }
    } catch (error) {
      console.error("Error fetching CDN files:", error);
    }

    setLoading(false);
    setLoadingMore(false);
  }, [getTokenSilently, isProd]);

  // Load items when path changes
  useEffect(() => {
    const currentPath = getCurrentPath();
    fetchItems(currentPath);
  }, [getCurrentPath, fetchItems]);

  // Navigate to a folder
  const navigateToFolder = (folderPath) => {
    const searchParams = new URLSearchParams();
    if (folderPath) {
      searchParams.set('path', folderPath);
    }
    history.push(`/cdn-browser${searchParams.toString() ? '?' + searchParams.toString() : ''}`);
  };

  // Handle file preview
  const handlePreview = (item) => {
    if (item.type === 'image') {
      const cdnBaseUrl = config.cdn[isProd ? "prod" : "dev"];
      const fileUrl = `${cdnBaseUrl}/${item.path}`;
      setPreviewImage(fileUrl);
      setPreviewTitle(item.name);
      setPreviewVisible(true);
    }
  };

  // Handle file actions
  const handleFileAction = (item, action) => {
    const cdnBaseUrl = config.cdn[isProd ? "prod" : "dev"];
    const fileUrl = `${cdnBaseUrl}/${item.path}`;
    
    if (action === 'view') {
      if (item.type === 'image') {
        handlePreview(item);
      } else {
        window.open(fileUrl, '_blank');
      }
    } else if (action === 'download') {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = item.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (action === 'delete') {
      setDeleteTarget(item);
      setDeleteConfirmText('');
    } else if (action === 'copy') {
      navigator.clipboard.writeText(fileUrl).then(() => {
        message.success('URL copied to clipboard!');
      }).catch(() => {
        message.error('Failed to copy URL');
      });
    }
  };

  // Perform file deletion
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const accessToken = await getTokenSilently();
      const env = isProd ? "prod" : "dev";
      const response = await fetch(
        `${config.baseUrl[env]}/cdn-delete`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ key: deleteTarget.path }),
        }
      );
      if (response.ok) {
        message.success('File eliminato con successo');
        setDeleteTarget(null);
        handleRefresh();
      } else {
        message.error('Errore durante l\'eliminazione del file');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      message.error('Errore durante l\'eliminazione del file');
    }
    setDeleting(false);
  };

  // Load more items
  const handleLoadMore = () => {
    if (continuationToken && !loadingMore) {
      const currentPath = getCurrentPath();
      fetchItems(currentPath, continuationToken);
    }
  };

  // Refresh the current view
  const handleRefresh = () => {
    const currentPath = getCurrentPath();
    fetchItems(currentPath);
  };

  // Create a new folder (navigate into it — it materializes in S3 on first upload)
  const handleNewFolder = () => {
    Modal.confirm({
      title: 'Nuova Cartella',
      content: (
        <Input
          id="new-folder-input"
          placeholder="Nome cartella"
          onPressEnter={(e) => {
            const modal = document.querySelector('.ant-modal-confirm-btns .ant-btn-primary');
            if (modal) modal.click();
          }}
        />
      ),
      okText: 'Crea',
      cancelText: 'Annulla',
      onOk: () => {
        const input = document.getElementById('new-folder-input');
        const name = input?.value?.trim();
        if (!name) return;
        const currentPath = getCurrentPath();
        const newPath = currentPath ? `${currentPath}${name}/` : `${name}/`;
        navigateToFolder(newPath);
      },
    });
  };

  // Table columns for list view
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => {
        const cdnBaseUrl = config.cdn[isProd ? "prod" : "dev"];
        const fileUrl = `${cdnBaseUrl}/${record.path}`;
        
        return (
          <Space>
            {record.type === 'image' ? (
              <div style={{
                width: 40,
                height: 40,
                border: '1px solid #f0f0f0',
                borderRadius: '4px',
                overflow: 'hidden',
                background: 'repeating-conic-gradient(#f0f0f0 0% 25%, transparent 0% 50%) 50% / 10px 10px',
                display: 'inline-block',
              }}>
                <Image
                  width={40}
                  height={40}
                  src={fileUrl}
                  preview={false}
                  style={{ 
                    objectFit: 'cover', 
                    cursor: 'pointer',
                    display: 'block',
                  }}
                  onClick={() => handlePreview(record)}
                />
              </div>
            ) : (
              getFileIcon(record.type)
            )}
            {record.type === 'folder' ? (
              <Button type="link" onClick={() => navigateToFolder(record.path)} style={{ padding: 0 }}>
                {text}
              </Button>
            ) : (
              <Text>{text}</Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      width: 120,
      render: (size, record) => record.type === 'folder' ? '-' : formatFileSize(size),
    },
    {
      title: 'Modified',
      dataIndex: 'lastModified',
      key: 'lastModified',
      width: 200,
      render: (date) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => {
        if (record.type === 'folder') {
          return (
            <Button
              type="link"
              onClick={() => navigateToFolder(record.path)}
            >
              Open
            </Button>
          );
        }
        
        return (
          <Space size="small">
            <Tooltip title="View">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => handleFileAction(record, 'view')}
              />
            </Tooltip>
            <Tooltip title="Copy URL">
              <Button
                type="link"
                icon={<CopyOutlined />}
                onClick={() => handleFileAction(record, 'copy')}
              />
            </Tooltip>
            <Tooltip title="Download">
              <Button
                type="link"
                icon={<DownloadOutlined />}
                onClick={() => handleFileAction(record, 'download')}
              />
            </Tooltip>
            <Tooltip title="Elimina">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleFileAction(record, 'delete')}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ margin: "16px 16px" }}>
      <div style={{ marginBottom: "20px" }}>
        <Breadcrumb>
          <Breadcrumb.Item>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a onClick={() => navigateToFolder('')}>
              <HomeOutlined /> Esplora Risorse CDN
            </a>
          </Breadcrumb.Item>
          {breadcrumbs.map((crumb, index) => (
            <Breadcrumb.Item key={index}>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a onClick={() => navigateToFolder(crumb.path)}>
                {crumb.name}
              </a>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>

      <div style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Title level={3} style={{ margin: 0 }}>
          <Space>
            <FolderOutlined />
            {breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].name : 'Esplora Risorse CDN'}
          </Space>
        </Title>
        
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            icon={<FolderAddOutlined />}
            onClick={handleNewFolder}
          >
            Nuova Cartella
          </Button>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={() => setShowUploader(!showUploader)}
          >
            Upload
          </Button>
        </Space>
      </div>

      {showUploader && (
        <div style={{ marginBottom: 20 }}>
          <CdnUploader
            path={getCurrentPath()}
            allowedFileTypes={["image/*", ".svg", ".pdf", ".woff", ".woff2", ".ttf"]}
            maxFileSize={50 * 1024 * 1024}
            maxNumberOfFiles={5}
            note="Immagini, PDF, SVG, font (max 50 MB)"
            onComplete={() => {
              handleRefresh();
            }}
          />
        </div>
      )}

      <Divider />

      {items.length === 0 ? (
        <Empty
          description="No files or folders found"
          style={{ marginTop: "50px" }}
        />
      ) : (
        <>
          <Table
            dataSource={items}
            columns={columns}
            rowKey={(record) => record.path}
            pagination={false}
            size="middle"
          />
          
          {isTruncated && (
            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <Button
                type="primary"
                loading={loadingMore}
                onClick={handleLoadMore}
              >
                Load More
              </Button>
            </div>
          )}
        </>
      )}

      <BackTop />

      <Modal
        visible={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={800}
      >
        <div style={{ 
          textAlign: 'center',
          padding: '20px',
          background: 'repeating-conic-gradient(#f9f9f9 0% 25%, #ffffff 0% 50%) 50% / 20px 20px',
          borderRadius: '4px',
        }}>
          <Image
            src={previewImage}
            style={{ maxWidth: '100%' }}
            preview={{
              visible: false,
              mask: null,
            }}
          />
        </div>
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Space>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => {
                const link = document.createElement('a');
                link.href = previewImage;
                link.download = previewTitle;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Download
            </Button>
            <Button
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(previewImage).then(() => {
                  message.success('URL copied to clipboard!');
                }).catch(() => {
                  message.error('Failed to copy URL');
                });
              }}
            >
              Copy URL
            </Button>
            <Button
              icon={<EyeOutlined />}
              onClick={() => window.open(previewImage, '_blank')}
            >
              Open in New Tab
            </Button>
          </Space>
        </div>
      </Modal>

      <Modal
        visible={!!deleteTarget}
        title={<span><DeleteOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />Elimina file</span>}
        width={500}
        onCancel={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}
        okText="Elimina definitivamente"
        okType="danger"
        okButtonProps={{ disabled: deleteConfirmText !== deleteTarget?.name, loading: deleting }}
        cancelText="Annulla"
        onOk={handleDelete}
      >
        <p>Questa azione è irreversibile. Per confermare, digita il nome del file:</p>
        <p><Text copyable strong>{deleteTarget?.name}</Text></p>
        <Input
          placeholder="Digita il nome del file per confermare"
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default CDNBrowser;
